import { memo } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import P1 from '@base/P1/P1';
import H3 from '@base/H3/H3';
import Button from '@base/Button/Button';
import { getImage } from '@utils/imageHelper';
import { breakpoints, Spacings } from '@styles/index';
import NoResults from 'aquilla/icons/emptyStates/NoResults';

const Empty = styled.div`
  grid-column: ${({ $gridColumn }) => $gridColumn};
  text-align: center;
  margin-top: ${({ $marginTop }) => $marginTop};
  ${({ $rawStyles }) => $rawStyles}
  display: flex;
  flex-direction: column;
  align-items: center;
  @media only screen and (max-width: ${breakpoints.mobile}) {
    grid-column: span 10;
    margin-top: ${Spacings.SPACING_10B};
  }
`;

const NoDataTitle = styled(H3)`
  margin-top: ${Spacings.SPACING_6B};
  margin-bottom: ${Spacings.SPACING_2B};
  @media only screen and (max-width: ${breakpoints.mobile}) {
    margin-top: ${Spacings.SPACING_2B};
    font-size: 16px;
  }
`;

const DescriptionP1 = styled(P1)`
  margin-bottom: ${Spacings.SPACING_4B};
  @media only screen and (max-width: ${breakpoints.mobile}) {
    color: var(--color-text-primary);
  }
`;

const EmptyImage = styled.img`
  user-drag: none;
  user-select: none;
  @media only screen and (max-width: ${breakpoints.mobile}) {
    width: 160px;
    height: 160px;
    object-fit: cover;
  }
`;

const EmptyState = ({
  imgSrc,
  title,
  description,
  gridColumn,
  marginTop,
  rawStyles,
  className,
  isCTAPresent,
  handleCTA,
  variantCTA,
  labelCTA
}) => {
  const renderCTAButton = () => {
    if (isCTAPresent) {
      return (
        <Button
          grid={3}
          variant={variantCTA}
          label={labelCTA}
          onClick={handleCTA}
          color="blue"
        />
      );
    }
  };

  return (
    <Empty
      $gridColumn={gridColumn}
      $marginTop={marginTop}
      $rawStyles={rawStyles}
      className={className}
    >
      {imgSrc ? (
        <EmptyImage src={imgSrc} alt="No data" />
      ) : (
        <NoResults height={Spacings.SPACING_30B} width={Spacings.SPACING_30B} />
      )}
      <NoDataTitle color="var(--color-text-primary)">{title}</NoDataTitle>
      <DescriptionP1 color="var(--color-text-secondary)">
        {description}
      </DescriptionP1>
      {renderCTAButton()}
    </Empty>
  );
};

EmptyState.propTypes = {
  imgSrc: PropTypes.string,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  gridColumn: PropTypes.string,
  marginTop: PropTypes.string,
  rawStyles: PropTypes.string,
  className: PropTypes.string,
  isCTAPresent: PropTypes.bool,
  handleCTA: PropTypes.func,
  variantCTA: PropTypes.string,
  labelCTA: PropTypes.string
};

EmptyState.defaultProps = {
  imgSrc: getImage('empty.svg'),
  gridColumn: '7/14',
  marginTop: Spacings.SPACING_21B,
  rawStyles: '',
  className: '',
  isCTAPresent: false,
  handleCTA: () => {},
  variantCTA: '',
  labelCTA: '',
  description: ''
};

export default memo(EmptyState);
